import {
  ClienteCrearDTO,
  DireccionDTO,
  DireccionEditarDTO,
} from 'src/app/clientes/utilidades/clientes.models';
import { EstadoFilaOrden } from './archivo-ordenes.models';
import {
  capitalizar,
  normalizarString,
  stringVacío,
  valorNumérico,
  valorVacío,
} from 'src/app/compartido/utilidades/valores.utils';
import {
  FilaSpreadsheet,
  Spreadsheet,
} from 'src/app/compartido/utilidades/spreadsheet.class';
import { COLUMNAS_ARCHIVO_ORDEN } from './archivo-ordenes.static';
import {
  ItemOrdenCrearDTO,
  OrdenCrearDTO,
} from 'src/app/ordenes/utilidades/ordenes.models';

/* Fila representando una orden en el archivo */
export class FilaOrden {
  /* Las celdas inválidas de la fila */
  public celdasInvalidas: { columna: string; error: string }[] = [];

  /* Las direcciones del cliente de la fila */
  public opcionesDireccion: DireccionDTO[] = [];

  constructor(
    public indice: number,
    public datos: OrdenCrearDTO,
    public estado: EstadoFilaOrden,
    public errores?: string,
  ) {}

  /**
   * Crea una instancia de FilaOrden a partir de una fila de una hoja de cálculo.
   * @param {Spreadsheet} spreadsheet - La hoja de cálculo de donde se obtiene la fila.
   * @param {number} indice - El índice de la fila en la hoja de cálculo.
   * @returns {FilaOrden} - La instancia de FilaOrden creada.
   * @author Juan Corral
   */
  public static fromSpreadsheet(
    spreadsheet: Spreadsheet,
    indice: number,
  ): FilaOrden {
    const fila = spreadsheet.obtenerFila(indice);

    // Determinar ciudad, localidad y barrio
    let ciudad = capitalizar(fila['ciudad']);
    if (ciudad.includes('-')) ciudad = ciudad.split('-')[0].trim();
    const localidad = capitalizar(fila['localidad']);
    const barrio = capitalizar(fila['barrio']);
    if (localidad.toLowerCase() == 'soacha') ciudad = 'Soacha';
    else if (normalizarString(localidad) == 'facatativa') ciudad = 'Facatativá';

    // Determinar departamento
    let departamento: string | undefined = undefined;
    if (ciudad.toLowerCase() == 'cali') departamento = 'Valle del Cauca';
    else if (ciudad.toLowerCase() == 'pereira') departamento = 'Risaralda';
    else if (ciudad.toLowerCase() == 'manizales') departamento = 'Caldas';
    else if (ciudad.toLowerCase() == 'armenia') departamento = 'Quindío';
    else if (ciudad.toLowerCase() == 'dosquebradas') departamento = 'Risaralda';

    // Crear dirección
    const direccion: DireccionEditarDTO = {
      direccion: fila['direccion']!,
      direccion_formateada: undefined,
      detalles: stringVacío(fila['detalles']) ? undefined : fila['detalles'],
      barrio: stringVacío(barrio) ? undefined : barrio,
      localidad: stringVacío(localidad) ? undefined : localidad,
      ciudad: stringVacío(ciudad) ? undefined : ciudad,
      departamento: departamento ?? undefined,
      latitud: valorNumérico(fila['latitud'], 'float')?.toString() ?? undefined,
      longitud:
        valorNumérico(fila['longitud'], 'float')?.toString() ?? undefined,
      creacion_manual: false,
    };

    // Determinar teléfono
    let telefono = undefined;
    let celular = undefined;
    let telStr = valorNumérico(fila['telefono'])?.toString();
    if (telStr && !stringVacío(telStr)) {
      if (telStr.length == 7) telefono = valorNumérico(telStr);
      else {
        if (telStr.startsWith('57')) telStr = telStr.substring(2);
        if (telStr.length === 10) celular = valorNumérico(telStr);
      }
    }

    // Determinar correo
    let correo: string | undefined = fila['correo'];
    if (stringVacío(correo) || !correo.includes('@')) correo = undefined;

    // Crear cliente
    const cliente: ClienteCrearDTO = {
      tipo_identificacion: fila['tipo_identificacion'] ?? undefined,
      identificacion: fila['identificacion']!,
      nombre: capitalizar(fila['nombre'])!,
      telefono: telefono ?? undefined,
      celular: celular ?? undefined,
      correo: correo,
      organizacion: valorNumérico(fila['id_organizacion'])!,
    };

    // Crear item
    const items: ItemOrdenCrearDTO[] = [];
    if (!valorVacío(fila['carga'])) {
      const cargas = fila['carga'].split(',');
      const unidades = fila['unidades'].split(',');
      for (let i = 0; i < cargas.length; i++) {
        const carga = cargas[i].trim();
        if (valorVacío(carga)) continue;
        const cantidad = unidades.at(i);
        const item = {
          cantidad: valorNumérico(cantidad) ?? 1,
          producto: {
            nombre: capitalizar(carga),
            id_externo: undefined,
          },
        };
        items.push(item);
      }
    }

    // Crear orden
    const orden: OrdenCrearDTO = {
      enumeracion: valorNumérico(fila['enumeracion']),
      referencia: fila['referencia'] ?? undefined,
      tipo: fila['tipo'] ? fila['tipo'].toLowerCase() : undefined,
      prioridad: fila['prioridad']
        ? fila['prioridad'].toLowerCase()
        : undefined,
      origen: valorNumérico(fila['origen']),
      centro_costo: fila['centro_costo'] ?? undefined,
      servicio: fila['servicio'] ?? undefined,
      recaudo: valorNumérico(fila['recaudo']),
      direccion: direccion,
      cliente: cliente,
      items: items,
      formulas: [],
      notas: fila['notas'] ?? undefined,
    };

    // Establecer celdas inválidas
    const celdasInvalidas: { columna: string; error: string }[] = [];
    for (const columna of COLUMNAS_ARCHIVO_ORDEN) {
      const valor = fila[columna.slug];
      if (columna.requerida && stringVacío(valor))
        celdasInvalidas.push({
          columna: `${columna.slug.toUpperCase()} (${columna.descripcion})`,
          error: 'Campo requerido',
        });
      else if (columna.validador && !stringVacío(valor)) {
        const resultado = columna.validador(valor);
        if (resultado !== null)
          celdasInvalidas.push({
            columna: `${columna.slug.toUpperCase()} (${columna.descripcion})`,
            error: resultado,
          });
      }
    }

    // Establecer estado
    const estado =
      celdasInvalidas.length > 0
        ? EstadoFilaOrden.INVALIDA
        : EstadoFilaOrden.SIN_FORMATEAR;

    // Crear instancia
    const filaOrden = new FilaOrden(indice, orden, estado);
    filaOrden.celdasInvalidas = celdasInvalidas;
    return filaOrden;
  }

  /**
   * Crea una instancia de FilaSpreadsheet a partir de los datos de la fila.
   * @returns {FilaSpreadsheet} - La instancia de FilaSpreadsheet creada.
   * @author Juan Corral
   */
  public toFilaSpreadsheet(): FilaSpreadsheet {
    const fila = {
      tipo: this.datos.tipo,
      prioridad: this.datos.prioridad,
      referencia: this.datos.referencia,
      servicio: this.datos.servicio,
      identificacion: this.datos.cliente.identificacion,
      nombre: this.datos.cliente.nombre,
      direccion: this.datos.direccion.direccion,
      direccion_formateada: this.datos.direccion.direccion_formateada ?? '',
      detalles: this.datos.direccion.detalles ?? '',
      barrio: this.datos.direccion.barrio ?? '',
      localidad: this.datos.direccion.localidad ?? '',
      ciudad: this.datos.direccion.ciudad ?? '',
      latitud: this.datos.direccion.latitud ?? '',
      longitud: this.datos.direccion.longitud ?? '',
      telefono: this.datos.cliente.telefono?.toString() ?? '',
      celular: this.datos.cliente.celular?.toString() ?? '',
      correo: this.datos.cliente.correo ?? '',
    } as FilaSpreadsheet;
    if (this.errores) fila['errores'] = this.errores;
    return fila;
  }
}
